import { AfterViewInit, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FilterService } from '@app/core/services/container/filter.service';
import { SidenavService } from '@app/core/services/container/sidenav.service';
import { Division } from '@app/features/division/models/division.interface';
import { Section, SectionValue } from '@app/features/division/models/section.interface';
import { DivisionService } from '@app/features/division/services/division.service';
import { SectionService } from '@app/features/division/services/section.service';
import { Constants, FieldType } from '@app/shared/constants';
import { DataSource } from '@components/data/data-table-new/datasource/datasource.component';
import { SearchFilter } from '@components/data/data-table-new/directives/datatable-search.directive';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { DataTableState } from '@components/data/data-table-new/state/state.model';
import { StateService } from '@components/data/data-table-new/state/state.service';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SectionValueToDivision } from '../../section-new.component';

@Component({
  selector: 'vfs-assign-division',
  templateUrl: './assign-division.component.html',
  styleUrls: ['./assign-division.component.scss']
})
export class AssignDivisionComponent implements OnInit, AfterViewInit {
  stateId = 'divisionsToAssign';
  dataSource = new DataSource<Division>();
  columns$: Observable<any[]>;
  displayedColumns: string[];
  @Input() selection: EntitySelectionModel<Division>;
  @Input() selected: Division[];
  @Input() sectionValuesToDivisions: SectionValueToDivision[];
  @Input() sectionValueId: string;
  @Input() all: boolean;
  @Input() filterByLoggedUser = true;
  @Input() embeddedMode = false;
  @Input() includeInactive = false;
  @Input() disabled = false;

  stateSubject$ = new Subject<DataTableState>();
  searchFilters: SearchFilter[] = [
    {
      name: 'description',
      type: FieldType.STRING
    }
  ];
  // Sections
  sections: Section[];
  // Division
  divisions$: Observable<any>;
  @Output() accepted = new EventEmitter<Division[]>();

  constructor(
    public sectionService: SectionService,
    @Inject('StateService') private stateService: StateService,
    protected divisionService: DivisionService,
    protected filterService: FilterService,
    protected sidenavService: SidenavService
  ) {
    this.columns$ = this.getColumnsDefinition().pipe(
      tap(columns => (this.displayedColumns = columns.map(column => column.id)))
    );
  }

  ngOnInit() {
    if (!this.selection) {
      this.selection = new EntitySelectionModel<Division>(true, []);
    }

    if (this.sectionValuesToDivisions && !this.selected) {
      const sectionValuesToDivision = this.sectionValuesToDivisions.find(
        sectionValueToDivisions => sectionValueToDivisions.name === this.sectionValueId
      );
      if (sectionValuesToDivision) {
        const divisionsSelected: Division[] = this.sectionValuesToDivisions.find(
          sectionValueToDivisions => sectionValueToDivisions.name === this.sectionValueId
        ).divisions;
        if (divisionsSelected) {
          this.selection.selected.push(...divisionsSelected);
        }
      }
    } else if (this.selected) {
      this.selection.select(...this.selected);
    }
  }

  ngAfterViewInit() {
    const state: DataTableState = this.stateService.get(this.stateId);
    state.sort.column = 'description';
    this.refreshData(state);
    this.stateSubject$.subscribe(newState => this.refreshData(newState));
  }

  private refreshData(state: DataTableState) {
    this.dataSource.loading = true;
    // const filters: string = this.filterService.buildFilterForSearchNew(state.filter, this.searchFilters);
    this.getDisplayedDivisions(state);
  }

  /**
   * Get the datatable columns definition
   */
  protected getColumnsDefinition(): Observable<string[]> {
    let columns: any[] = [
      {
        id: 'select',
        label: ''
      },
      {
        id: 'description',
        label: 'Description'
      }
    ];
    return this.sectionService.getAllWithoutPageable().pipe(
      tap(sections => (this.sections = sections)),
      map(sections => {
        sections.forEach(section => {
          columns.push({ id: section.id, label: section.name });
        });
        return columns;
      })
    );
  }

  protected getDisplayedDivisions(state: DataTableState) {
    let divisionsToShow = [];
    if (!this.all) {
      const otherSelectedDivisions = this.sectionValuesToDivisions
        .filter(sectionValuesToDivision => sectionValuesToDivision.name !== this.sectionValueId)
        .map(sectionValuesToDivision => sectionValuesToDivision.divisions)
        .reduce((acc, divisions) => {
          acc.push(...divisions);
          return acc;
        }, []);
      this.divisionService
        .getVisible(!this.filterByLoggedUser, this.includeInactive)
        .pipe(
          tap(divisions => {
            divisions
              .filter(division => !otherSelectedDivisions.find(divisionForFind => divisionForFind.id === division.id))
              .filter(division => this.normalize(division.description).includes(this.normalize(state.filter.search)))
              .forEach(division => {
                let current = {
                  description: division.description,
                  active: division.active,
                  id: division.id,
                  divisionSectionValues: division.divisionSectionValues
                };
                division.divisionSectionValues.forEach(dsv => {
                  // FIXME: Rethink this
                  if ((dsv.sectionValue as SectionValue).section !== undefined) {
                    let section = (dsv.sectionValue as SectionValue).section;
                    current[section as string] = (dsv.sectionValue as SectionValue).description;
                  }
                });
                divisionsToShow.push(current);
              });
            let filteredDivisions = this.searchAndSortDivisions(divisionsToShow, state);
            this.dataSource.setData(filteredDivisions);
            this.dataSource.loading = false;
          })
        )
        .subscribe();
    } else {
      this.divisionService
        .getVisible(!this.filterByLoggedUser)
        .pipe(
          tap(divisions => {
            divisions.forEach(division => {
              let current = {
                description: division.description,
                active: division.active,
                id: division.id,
                divisionSectionValues: division.divisionSectionValues
              };
              division.divisionSectionValues.forEach(dsv => {
                // FIXME: Rethink this
                if ((dsv.sectionValue as SectionValue).section !== undefined) {
                  let section = (dsv.sectionValue as SectionValue).section;
                  current[section as string] = (dsv.sectionValue as SectionValue).description;
                }
              });
              divisionsToShow.push(current);
            });
            let filteredDivisions = this.searchAndSortDivisions(divisionsToShow, state);
            this.dataSource.setData(filteredDivisions);
            this.dataSource.loading = false;
          })
        )
        .subscribe();
    }
  }

  getSearchFilters(): Observable<SearchFilter[]> {
    let searchFilters: SearchFilter[] = [];
    return this.sectionService.getAllWithoutPageable().pipe(
      map(sections => {
        sections.forEach(section => {
          searchFilters.push({
            name: section.name,
            type: FieldType.STRING
          });
        });
        return searchFilters;
      })
    );
  }

  protected normalize(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  masterToggle() {
    this.selection.selected.length === this.dataSource.data.length
      ? this.selection.clear()
      : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  accept() {
    this.accepted.emit(this.selection.selected);
    this.sidenavService.closeSidenav();
  }

  cancel() {
    this.sidenavService.closeSidenav();
  }

  searchAndSortDivisions(divisions: any[], state: DataTableState) {
    divisions.sort((a, b) => {
      if (state.sort.direction === Constants.ASC_DIR) {
        return this.localeCompare(a[state.sort.column], b[state.sort.column]);
      } else {
        return this.localeCompare(b[state.sort.column], a[state.sort.column]);
      }
    });

    let divisionsFiltered = new Set<Division>();
    this.getDivisionFieldsToSearch(divisions).forEach(field => {
      divisions
        .filter(division => this.compareField(division[field], state.filter.search))
        .forEach(divisionFiltered => divisionsFiltered.add(divisionFiltered));
    });

    return Array.from(divisionsFiltered);
  }

  protected getDivisionFieldsToSearch(divisions: any[]): any[] {
    let divisionValueIds = Object.keys(divisions[0]);
    divisionValueIds.splice(divisionValueIds.indexOf('active'), 1);
    divisionValueIds.splice(divisionValueIds.indexOf('id'), 1);
    divisionValueIds.splice(divisionValueIds.indexOf('divisionSectionValues'), 1);
    return divisionValueIds;
  }

  protected compareField(field: string, search: string) {
    return field ? this.normalize(field).includes(this.normalize(search)) : false;
  }

  onClickToggle(row) {
    if (!this.disabled) {
      this.selection.toggle(row);
    }
  }

  protected localeCompare(s1: string, s2: string) {
    if (s1 != null) {
      return s1.localeCompare(s2);
    } else if (s2 != null) {
      return 1;
    }
    return 0;
  }
}
